/**
 * Staff-facing EPA ID tracking for waste facilities: crosses "does the
 * governing federal generator status require an EPA ID" with "is a live EPA
 * ID recorded". Null/absent for facilities without the waste program.
 */
export const EPA_ID_REQUIREMENT_STATUSES = [
  "HAS_EPA_ID",
  "NEEDS_EPA_ID",
  "EPA_ID_EXPIRED",
  "NOT_REQUIRED",
  "NOT_REQUIRED_HAS_EPA_ID",
] as const;

export type EpaIdRequirementStatus = (typeof EPA_ID_REQUIREMENT_STATUSES)[number];

export function isEpaIdRequirementStatus(value: string): value is EpaIdRequirementStatus {
  return (EPA_ID_REQUIREMENT_STATUSES as readonly string[]).includes(value);
}

/** States whose programs require even federal VSQGs to obtain an EPA ID. */
export const STATES_REQUIRING_EPA_ID_FOR_VSQG = [
  "CA",
  "LA",
  "MA",
  "ME",
  "MN",
  "NH",
  "RI",
  "VT",
  "WV",
] as const;

export function stateRequiresEpaIdForVsqg(state: string | null | undefined): boolean {
  return state != null && (STATES_REQUIRING_EPA_ID_FOR_VSQG as readonly string[]).includes(state);
}

/** States where shipping on a Uniform Hazardous Waste Manifest requires an EPA ID. */
export const STATES_REQUIRING_EPA_ID_FOR_UNIFORM_MANIFEST = ["WI"] as const;

export function stateRequiresEpaIdForUniformManifest(state: string | null | undefined): boolean {
  return (
    state != null &&
    (STATES_REQUIRING_EPA_ID_FOR_UNIFORM_MANIFEST as readonly string[]).includes(state)
  );
}

export const EPA_ID_REQUIREMENT_STATUS_LABELS: Record<EpaIdRequirementStatus, string> = {
  HAS_EPA_ID: "Has EPA ID",
  NEEDS_EPA_ID: "Needs EPA ID",
  EPA_ID_EXPIRED: "EPA ID expired",
  NOT_REQUIRED: "Not required",
  NOT_REQUIRED_HAS_EPA_ID: "Has EPA ID (not required)",
};

/** Why a facility requires an EPA ID — one entry per rule that fired. */
export const EPA_ID_REQUIREMENT_REASONS = [
  "GENERATOR_STATUS",
  "HISTORICAL_GENERATOR_STATUS",
  "STATE_VSQG_RULE",
  "STATE_UNIFORM_MANIFEST_RULE",
  "EPISODIC_EVENT",
] as const;

export type EpaIdRequirementReason = (typeof EPA_ID_REQUIREMENT_REASONS)[number];

export const EPA_ID_REQUIREMENT_REASON_LABELS: Record<EpaIdRequirementReason, string> = {
  GENERATOR_STATUS: "SQG/LQG generator status",
  HISTORICAL_GENERATOR_STATUS: "Prior-year generator status (no current-year data)",
  STATE_VSQG_RULE: "State requires VSQGs to hold an EPA ID",
  STATE_UNIFORM_MANIFEST_RULE:
    "State requires an EPA ID to ship on a Uniform Hazardous Waste Manifest",
  EPISODIC_EVENT: "Recorded an episodic event, which requires an EPA ID (40 CFR 262 subpart L)",
};

/** Display label for a status; unknown values fall through unchanged. */
export function epaIdRequirementStatusLabel(value: string): string {
  return isEpaIdRequirementStatus(value) ? EPA_ID_REQUIREMENT_STATUS_LABELS[value] : value;
}

/** Export-cell label: empty string for non-waste facilities (null/undefined). */
export function epaIdRequirementStatusExportLabel(
  status: EpaIdRequirementStatus | null | undefined
): string {
  return status ? EPA_ID_REQUIREMENT_STATUS_LABELS[status] : "";
}

/**
 * The federal tiers that carry obligations of their own — an EPA ID and
 * notification under 40 CFR 262.18, which is scoped to small and large quantity
 * generators. A very small quantity generator is exempt from both (262.14(a)
 * excludes VSQG waste from part 262 outside §§ 262.10-262.14 and from RCRA
 * §3010 notification), so no federal duty attaches to the VSQG tier itself.
 *
 * Exported as an array so raw-SQL callers can build the same list rather than
 * restating it; the Set and the predicate below are the TypeScript readers.
 */
export const FEDERAL_TIERS_CARRYING_OBLIGATION = [
  "LARGE_QUANTITY_GENERATOR",
  "SMALL_QUANTITY_GENERATOR",
] as const;

const EPA_ID_REQUIRING_FEDERAL_STATUSES: ReadonlySet<string> = new Set(
  FEDERAL_TIERS_CARRYING_OBLIGATION
);

/**
 * Whether a calculated federal tier could be under-reporting a federal
 * obligation, i.e. whether it can raise a federal exceedance at all.
 *
 * A VSQG month cannot: the tier owes nothing federally, so there is no duty for
 * a lower reported value to be short of. The comparison's rank ladder puts VSQG
 * above "not a generator", which is true as a quantity ordering and misleading
 * as a compliance one. The 262.14 accumulation triggers (>1 kg acute or
 * >1,000 kg non-acute on site) DO pull a VSQG into 262.18, but those are
 * on-site accumulation conditions tracked by container accumulation areas, not
 * by this monthly generation tier.
 */
export function federalTierCarriesObligation(status: string | null | undefined): boolean {
  return EPA_ID_REQUIRING_FEDERAL_STATUSES.has(status ?? "");
}

/**
 * The same tiers as a quoted SQL list, for raw-query callers that cannot reach
 * the predicate above. Lives here so the TypeScript and SQL readers cannot
 * drift. The values are compile-time literals, never user input.
 */
export const federalObligationTiersSqlList = (): string =>
  FEDERAL_TIERS_CARRYING_OBLIGATION.map((tier) => `'${tier}'`).join(", ");

/**
 * Whether a month has no reported generator status to compare a calculated
 * STATE status against.
 *
 * The federal axis no longer needs this — `federalTierCarriesObligation` settles
 * it, and the SQL comparison applies that directly. The state axis still does:
 * both reported statuses are read from RCRAInfo through the same EPA ID, so they
 * go null together, and `effectiveReportedStateStatus(null, null)` ranks 0,
 * which would make any concrete calculated state status an exceedance against a
 * baseline that does not exist.
 *
 * Callers pass the whole month; the reported-status check lives here so a caller
 * cannot apply the requirement rule and forget it.
 */
export function shouldSkipThresholdComparison({
  reportedFederalStatus,
  requirementStatus,
  calculatedFederalStatus,
}: {
  reportedFederalStatus: string | null | undefined;
  requirementStatus: EpaIdRequirementStatus | null | undefined;
  calculatedFederalStatus: string | null | undefined;
}): boolean {
  // A reported status exists: compare against it.
  if (reportedFederalStatus != null) {
    return false;
  }
  // The facility owes an EPA ID (or holds one). Reported status is read from
  // RCRAInfo through that ID, so its absence is the finding, not an exemption.
  if (requirementStatus !== "NOT_REQUIRED") {
    return false;
  }
  // The facility needs no EPA ID overall, but the requirement is derived from
  // the `FacilityEPAIds` view, which only looks at the current year. An
  // earlier, still-viewable month that reached SQG/LQG needed one at the time.
  return !EPA_ID_REQUIRING_FEDERAL_STATUSES.has(calculatedFederalStatus ?? "");
}

/**
 * Presence and requirement classify independently (a VSQG can hold an ID).
 * With no live ID, an expired one wins over the requirement axis — the
 * lapsed association is the more specific gap.
 */
export function classifyEpaIdRequirementStatus({
  requiresEpaId,
  hasLiveEpaId,
  hasExpiredEpaId,
}: {
  requiresEpaId: boolean;
  hasLiveEpaId: boolean;
  hasExpiredEpaId: boolean;
}): EpaIdRequirementStatus {
  if (hasLiveEpaId) {
    return requiresEpaId ? "HAS_EPA_ID" : "NOT_REQUIRED_HAS_EPA_ID";
  }
  if (hasExpiredEpaId) {
    return "EPA_ID_EXPIRED";
  }
  return requiresEpaId ? "NEEDS_EPA_ID" : "NOT_REQUIRED";
}
